import { TbFillBolt, TbOutlineAccessPoint } from "solid-icons/tb";
import { createMemo, For, type JSX, type ParentProps, Show } from "solid-js";
import { useI18n } from "@/api/i18n";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import type { RemotePolicy } from "@/domains/job-offer/create/constants";
import { cn } from "@/libs/cn";
import { formatNumber, getInitials } from "@/libs/format";
import type { FreelanceBusinessCardData } from "./types";

/** Slot order: avatar → name → role·city → skills → rate → availability. */
export const BUSINESS_CARD_SLOT_COUNT = 6;

const VISIBLE_SKILLS = 3;

export type FreelanceBusinessCardProps = {
  data: FreelanceBusinessCardData;
  /**
   * How many slots have been filled in, 0..6. Defaults to a fully revealed
   * card, which is what static (decorative) usages want.
   */
  revealedSlots?: number;
  /** The card is "alive": Online badge, primary ring, shine sweep. */
  live?: boolean;
  /** Set to false (or with `prefers-reduced-motion`) to jump to final states. */
  animated?: boolean;
  /**
   * "progress" (default) draws the primary border that doubles as a slot
   * progress bar. "static" suppresses it, leaving the Card's neutral border —
   * for decorative usages where a fully-revealed, non-live card would
   * otherwise keep a solid primary ring.
   */
  border?: "progress" | "static";
  class?: string;
};

type SlotProps = ParentProps<{
  revealed: boolean;
  animated: boolean;
  skeleton: JSX.Element;
  class?: string;
}>;

/**
 * A single card field with two stacked layers: the shimmering placeholder and
 * the real value. Both layers are always mounted so the card never reflows
 * when a slot fills in.
 */
const Slot = (props: SlotProps) => (
  <div class={cn("relative", props.class)}>
    <div
      aria-hidden="true"
      classList={{
        "transition-opacity duration-[400ms] ease-out": props.animated,
        // Hidden behind the real value, so stop its infinite animation too:
        // the success screen is a terminal state the user can sit on.
        "opacity-0 [&_*]:[animation-play-state:paused]": props.revealed,
      }}
    >
      {props.skeleton}
    </div>
    <div
      class="absolute inset-0 flex items-center"
      // A slot that has not been filled in yet is not announced either: the
      // value is mounted only so the card never reflows when it appears.
      aria-hidden={props.revealed ? undefined : "true"}
      classList={{
        "transition-[opacity,transform] duration-[400ms] ease-out":
          props.animated,
        "opacity-0 translate-y-1.5": !props.revealed,
        "opacity-100 translate-y-0": props.revealed,
      }}
    >
      {props.children}
    </div>
  </div>
);

/** Placeholder block; `skeleton-shimmer` flattens itself under reduced motion. */
const Shimmer = (props: { class?: string }) => (
  <div class={cn("skeleton-shimmer rounded-md", props.class)} />
);

/**
 * The freelance "business card": the visual a company sees when it finds a
 * freelancer. Purely presentational — it knows nothing about onboarding, so it
 * can also be dropped anywhere as a static decorative visual
 * (`revealedSlots={6} live animated={false}` with arbitrary data).
 */
export const FreelanceBusinessCard = (props: FreelanceBusinessCardProps) => {
  const { t } = useI18n();

  const animated = () => props.animated !== false;
  const revealed = () => props.revealedSlots ?? BUSINESS_CARD_SLOT_COUNT;
  const isRevealed = (slot: number) => revealed() >= slot;

  const visibleSkills = createMemo(() =>
    props.data.skills.slice(0, VISIBLE_SKILLS),
  );
  const hiddenSkillCount = createMemo(() =>
    Math.max(props.data.skills.length - VISIBLE_SKILLS, 0),
  );
  const role = createMemo(() =>
    props.data.residency
      ? `${props.data.jobTitle} · ${props.data.residency}`
      : props.data.jobTitle,
  );
  const rate = createMemo(() =>
    formatNumber(props.data.pricePerDay, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }),
  );
  const borderOffset = createMemo(
    () => 100 - (revealed() / BUSINESS_CARD_SLOT_COUNT) * 100,
  );

  return (
    <Card
      class={cn(
        "relative flex w-full max-w-sm flex-col gap-5 p-6",
        props.class,
      )}
      classList={{
        "transition-[box-shadow,transform] duration-500 ease-out": animated(),
        "-translate-y-1.5 scale-[1.02] shadow-[0_0_0_2px_hsl(var(--primary)),0_16px_40px_-12px_hsl(var(--primary)/0.35)]":
          props.live,
      }}
    >
      {/* The card border doubles as a progress bar while the slots fill in.
          An <svg> is a replaced element: `absolute inset-px` alone leaves its
          height at the intrinsic 150px, so the perimeter rect would trace only
          the top of the card. A definite calc size forces it to fill. */}
      <Show when={props.border !== "static"}>
        <svg
          aria-hidden="true"
          class="pointer-events-none absolute left-px top-px h-[calc(100%-2px)] w-[calc(100%-2px)] overflow-visible"
          classList={{
            "transition-opacity duration-[400ms] ease-out": animated(),
            "opacity-0": props.live,
          }}
        >
          <rect
            x="0"
            y="0"
            width="100%"
            height="100%"
            rx="11"
            pathLength="100"
            fill="none"
            class="stroke-primary stroke-2"
            classList={{
              "transition-[stroke-dashoffset] duration-[550ms] ease-out":
                animated(),
            }}
            style={{
              "stroke-dasharray": "100",
              "stroke-dashoffset": String(borderOffset()),
            }}
          />
        </svg>
      </Show>

      {/* Inner layer so the sweep never clips the "Online" badge. */}
      <Show when={props.live && animated()}>
        <div
          aria-hidden="true"
          class="pointer-events-none absolute inset-0 overflow-hidden rounded-[inherit]"
        >
          <div class="shine-sweep absolute inset-0" />
        </div>
      </Show>

      <Show when={props.live}>
        <Badge
          class="absolute -top-3 right-4 gap-1.5 border-transparent bg-green-100 px-2.5 text-green-800 shadow-sm"
          classList={{ "pop-in": animated() }}
        >
          <TbOutlineAccessPoint aria-hidden="true" />
          {t("freelance_business_card.online")}
        </Badge>
      </Show>

      <div class="flex items-center gap-4">
        <Slot
          revealed={isRevealed(1)}
          animated={animated()}
          skeleton={<Shimmer class="size-16 rounded-full" />}
        >
          <Show
            when={props.data.avatarUrl}
            fallback={
              <div class="grid size-16 place-items-center rounded-full bg-primary/10 font-display text-xl font-bold text-primary">
                {getInitials(props.data.fullName)}
              </div>
            }
          >
            {(avatarUrl) => (
              <img
                src={avatarUrl()}
                alt={props.data.fullName}
                class="size-16 rounded-full object-cover"
              />
            )}
          </Show>
        </Slot>
        <div class="flex min-w-0 flex-1 flex-col gap-1.5">
          <Slot
            revealed={isRevealed(2)}
            animated={animated()}
            class="h-6"
            skeleton={<Shimmer class="h-5 w-36" />}
          >
            <span class="block w-full truncate font-display text-lg font-semibold leading-tight">
              {props.data.fullName}
            </span>
          </Slot>
          {/* Real job titles are long (full LinkedIn headlines): reserve two
              lines with readable leading and clamp the rest, so the value never
              overflows its slot and overlaps the name above. */}
          <Slot
            revealed={isRevealed(3)}
            animated={animated()}
            class="h-10"
            skeleton={
              <div class="flex flex-col gap-1.5">
                <Shimmer class="h-3.5 w-44" />
                <Shimmer class="h-3.5 w-28" />
              </div>
            }
          >
            <span class="line-clamp-2 text-sm leading-snug text-muted-foreground">
              {role()}
            </span>
          </Slot>
        </div>
      </div>

      <Slot
        revealed={isRevealed(4)}
        animated={animated()}
        class="h-7"
        skeleton={
          <div class="flex gap-2">
            <Shimmer class="h-6 w-16" />
            <Shimmer class="h-6 w-20" />
            <Shimmer class="h-6 w-14" />
          </div>
        }
      >
        {/* The slot is one chip tall and, like the others, never grows: real
            skills are long ("Gestione del ciclo di vita del prodotto"), and a
            wrapping row spilled over the title above and the divider below.
            One row of `minmax(0, max-content)` columns instead: the grid
            shares the width equally and freezes each chip at its own width,
            so short chips stay whole and only the long ones get an ellipsis. */}
        <div class="grid w-full auto-cols-[minmax(0,max-content)] grid-flow-col gap-2">
          <For each={visibleSkills()}>
            {(skill) => (
              <Badge variant="secondary" class="min-w-0" title={skill}>
                <span class="truncate">{skill}</span>
              </Badge>
            )}
          </For>
          <Show when={hiddenSkillCount() > 0}>
            <Badge variant="outline">
              {t("freelance_business_card.more_skills", {
                count: hiddenSkillCount(),
              })}
            </Badge>
          </Show>
        </div>
      </Slot>

      <div class="border-t" />

      <div class="grid grid-cols-2 gap-4">
        <div class="flex flex-col gap-1.5">
          <span class="text-xs font-medium text-muted-foreground">
            {t("freelance_business_card.rate_label")}
          </span>
          <Slot
            revealed={isRevealed(5)}
            animated={animated()}
            class="h-7"
            skeleton={<Shimmer class="h-7 w-24" />}
          >
            <span class="font-display text-2xl font-bold leading-none">
              {`€ ${rate()}`}
              <span class="text-sm font-medium text-muted-foreground">
                {t("freelance_business_card.rate_per_day_suffix")}
              </span>
            </span>
          </Slot>
        </div>
        <div class="flex flex-col gap-1.5">
          <span class="text-xs font-medium text-muted-foreground">
            {t("freelance_business_card.availability_label")}
          </span>
          <Slot
            revealed={isRevealed(6)}
            animated={animated()}
            class="h-7"
            skeleton={<Shimmer class="h-7 w-28" />}
          >
            <span class="inline-flex items-center gap-1.5 text-sm font-medium leading-none">
              <TbFillBolt class="text-primary" aria-hidden="true" />
              <AvailabilityLabel
                availabilityNow={props.data.availabilityNow}
                workPreference={props.data.workPreference}
              />
            </span>
          </Slot>
        </div>
      </div>
    </Card>
  );
};

const AvailabilityLabel = (props: {
  availabilityNow: boolean;
  workPreference?: RemotePolicy;
}) => {
  const { t } = useI18n();
  const availability = () =>
    props.availabilityNow
      ? t("freelance_business_card.available_now")
      : t("availability.unavailable");
  return (
    <span>
      {availability()}
      <Show when={props.workPreference}>
        {(preference) =>
          ` · ${t(`common.remote_policy_options.${preference()}`)}`
        }
      </Show>
    </span>
  );
};
